import React, { useMemo } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Alert } from '@mui/material';
import {
  Category as CategoryIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  History as HistoryIcon,
  Restore as RestoreIcon,
  ReceiptLong as ReceiptLongIcon,
  Payments as PaymentsIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import CategorySettingsPage from './settings/CategorySettingsPage';
import ProfileSettingsPage from './settings/ProfileSettingsPage';
import RestoreCenterPage from './settings/RestoreCenterPage';
import { AdminPermissionKey } from '../constants/adminPermissions';

interface SettingsCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  path: string;
  permission?: AdminPermissionKey;
  superAdminOnly?: boolean;
}

const SettingsHub: React.FC = () => {
  const navigate = useNavigate();
  const { isSuperAdmin, hasPermission } = useAuth();

  const cards: SettingsCard[] = [
    {
      id: 'categories',
      title: 'Blog Site Categories',
      description: 'Manage categories used across guest blog sites',
      icon: <CategoryIcon />,
      color: '#3b82f6',
      path: '/settings/categories',
    },
    {
      id: 'profile',
      title: 'Profile Settings',
      description: 'View your account profile and details',
      icon: <PersonIcon />,
      color: '#8b5cf6',
      path: '/settings/profile',
    },
    {
      id: 'users',
      title: 'Users & Roles',
      description: 'Manage team members and their access',
      icon: <GroupIcon />,
      color: '#10b981',
      path: '/users',
      permission: 'users',
    },
    {
      id: 'invoices',
      title: 'Client Billing & Invoices',
      description: 'View and manage client invoices',
      icon: <ReceiptLongIcon />,
      color: '#f59e0b',
      path: '/invoices',
      permission: 'invoices',
    },
    {
      id: 'billing',
      title: 'Publisher Billing & Invoices',
      description: 'View and manage publisher billing',
      icon: <PaymentsIcon />,
      color: '#6366f1',
      path: '/admin-billing',
      permission: 'admin_billing',
    },
    {
      id: 'audit',
      title: 'Audit Logs',
      description: 'Review system activity and changes',
      icon: <HistoryIcon />,
      color: '#ef4444',
      path: '/audit',
      permission: 'audit',
    },
    {
      id: 'restore',
      title: 'Restore Archived Data',
      description: 'Re-publish archived clients, projects, publishers, sites, and placements (7-day window)',
      icon: <RestoreIcon />,
      color: '#0ea5e9',
      path: '/settings/restore',
      superAdminOnly: true,
    },
  ];

  const visibleCards = useMemo(
    () =>
      cards.filter((card) => {
        if (card.superAdminOnly) return isSuperAdmin;
        if (!card.permission) return true;
        return isSuperAdmin || hasPermission(card.permission);
      }),
    [isSuperAdmin, hasPermission],
  );

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b', mb: 0.5 }}>
          Settings
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          Configure and manage your application
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 2.5,
        }}
      >
        {visibleCards.map((card) => (
          <Paper
            key={card.id}
            onClick={() => navigate(card.path, { state: { fromSettings: true } })}
            sx={{
              p: 2.5,
              borderRadius: 2,
              cursor: 'pointer',
              border: '1px solid #e2e8f0',
              boxShadow: 'none',
              transition: 'all 0.15s ease',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              '&:hover': {
                borderColor: card.color,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                transform: 'translateY(-2px)',
              },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `${card.color}1a`,
                color: card.color,
                flexShrink: 0,
                '& svg': { fontSize: 24 },
              }}
            >
              {card.icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#1e293b', mb: 0.5 }}>
                {card.title}
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#64748b', lineHeight: 1.45 }}>
                {card.description}
              </Typography>
            </Box>
            <ChevronRightIcon sx={{ color: '#94a3b8', flexShrink: 0 }} />
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

const SettingsPage: React.FC = () => {
  const { isSuperAdmin, hasPermission } = useAuth();

  if (!isSuperAdmin && !hasPermission('settings')) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          Access denied. You do not have permission to access Settings.
        </Alert>
      </Box>
    );
  }

  return (
    <Routes>
      <Route index element={<SettingsHub />} />
      <Route path="categories" element={<CategorySettingsPage />} />
      <Route path="profile" element={<ProfileSettingsPage />} />
      {isSuperAdmin && <Route path="restore" element={<RestoreCenterPage />} />}
      <Route path="*" element={<SettingsHub />} />
    </Routes>
  );
};

export default SettingsPage;
